'use client';

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, Upload, X, Sparkles, FileText, Loader2, RefreshCw, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { shake256HashContent } from '../../data/shake256-hash-content';

// Import SHA3 library (js-sha3 is already in your project)
const { shake256 } = require('js-sha3');

// Sample data
const sampleText = 'Hello, world! This is a sample text for SHAKE256 hashing.';
const defaultOutputLength = 64; // Default output length in bytes (512 bits)

type TabValue = 'text' | 'file';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export default function Shake256HashGenerator() {
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [outputLength, setOutputLength] = useState<number>(defaultOutputLength);
    const { toast } = useToast();

    // Function to generate SHAKE256 hash
    // Function to generate SHAKE256 hash
    // Function to generate SHAKE256 hash
    // Function to generate SHAKE256 hash
    const generateShake256Hash = useCallback((data: string, length: number): string => {
        try {
            // For js-sha3, we use the shake256 function directly with the data and output length
            // The length is in bits, so we multiply by 8
            return shake256(data, length * 8);
        } catch (error) {
            console.error('Error generating SHAKE256 hash:', error);
            throw new Error('Failed to generate hash');
        }
    }, []);
    // Generate hash when input or output length changes
    useEffect(() => {
        if (!input) {
            setHash('');
            return;
        }

        setIsLoading(true);

        // Use setTimeout to prevent blocking the UI
        const timer = setTimeout(() => {
            try {
                const generatedHash = generateShake256Hash(input, outputLength);
                setHash(generatedHash);
            } catch (error) {
                console.error('Error generating hash:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to generate hash. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setIsLoading(false);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [input, outputLength, generateShake256Hash, toast]);

    // Handle text input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    };

    // Handle file input change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            processFile(selectedFile);
        }
    };

    // Process the uploaded file
    const processFile = (file: File) => {
        setFile(file);
        setFileInfo({
            name: file.name,
            size: file.size,
            type: file.type || 'Unknown',
        });

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);
    };

    // Handle drag and drop
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    // Handle drop
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    // Handle sample text
    const handleUseSample = () => {
        setInput(sampleText);
        setFile(null);
        setFileInfo(null);
    };

    // Reset input
    const resetInput = () => {
        setInput('');
        setFile(null);
        setFileInfo(null);
    };

    // Copy hash to clipboard
    const copyToClipboard = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash).then(() => {
            setIsCopied(true);
            toast({
                title: 'Copied!',
                description: 'Hash has been copied to clipboard.',
            });

            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        });
    };

    // Handle output length change
    const handleOutputLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 1 && value <= 8192) {
            setOutputLength(value);
        }
    };

    // Generate hash when input or output length changes
    useEffect(() => {
        if (!input) {
            setHash('');
            return;
        }

        setIsLoading(true);

        const timer = setTimeout(async () => {
            try {
                const generatedHash = await generateShake256Hash(input, outputLength);
                setHash(generatedHash);
            } catch (error) {
                console.error('Error generating hash:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to generate hash. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setIsLoading(false);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [input, outputLength, generateShake256Hash, toast]);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="SHAKE-256 Hash Generator">
                Generate SHAKE-256 (extendable-output) hashes. Useful for variable-length digests. Paste or upload for instant results.
            </ToolPageIntro>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Input</CardTitle>
                            <CardDescription>
                                Enter your text or upload a file to generate its SHAKE256 hash.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="text" onValueChange={(value) => setActiveTab(value as TabValue)}>
                                <TabsList className="grid w-full grid-cols-2 mb-4">
                                    <TabsTrigger value="text">Text</TabsTrigger>
                                    <TabsTrigger value="file">File</TabsTrigger>
                                </TabsList>

                                <TabsContent value="text">
                                    <Textarea
                                        placeholder="Enter text to generate SHAKE256 hash..."
                                        className="min-h-[200px]"
                                        value={input}
                                        onChange={handleInputChange}
                                    />
                                    <div className="flex justify-between mt-2">
                                        <div className="text-sm text-muted-foreground">
                                            {input.length} characters
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleUseSample}
                                            disabled={isLoading}
                                        >
                                            <Sparkles className="h-4 w-4 mr-2" />
                                            Use Sample Text
                                        </Button>
                                    </div>
                                </TabsContent>

                                <TabsContent value="file">
                                    <div
                                        className={cn(
                                            'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                                            dragActive ? 'border-primary bg-primary/10' : 'border-border',
                                            fileInfo ? 'border-green-500' : ''
                                        )}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <Upload className="h-12 w-12 text-muted-foreground" />
                                            <div className="text-sm text-muted-foreground">
                                                <label
                                                    htmlFor="file-upload"
                                                    className="relative cursor-pointer font-medium text-primary hover:text-primary/80"
                                                >
                                                    <span>Upload a file</span>
                                                    <input
                                                        id="file-upload"
                                                        name="file-upload"
                                                        type="file"
                                                        className="sr-only"
                                                        onChange={handleFileChange}
                                                    />
                                                </label>{' '}
                                                or drag and drop
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Text files up to 10MB
                                            </p>
                                        </div>

                                        {fileInfo && (
                                            <div className="mt-4 p-3 bg-muted/50 rounded-md flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                                    <div className="text-sm">
                                                        <p className="font-medium truncate max-w-[200px]">
                                                            {fileInfo.name}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {Math.round(fileInfo.size / 1024)} KB · {fileInfo.type}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        resetInput();
                                                    }}
                                                >
                                                    <X className="h-4 w-4" />
                                                    <span className="sr-only">Remove file</span>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <CardTitle>SHAKE256 Hash</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <Hash className="h-5 w-5 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                        Output length: {outputLength} bytes
                                    </span>
                                </div>
                            </div>
                            <CardDescription>
                                The generated SHAKE256 hash will appear here.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="output-length">Output Length (bytes)</Label>
                                    <div className="flex items-center space-x-4">
                                        <Input
                                            id="output-length"
                                            type="number"
                                            min="1"
                                            max="8192"
                                            value={outputLength}
                                            onChange={handleOutputLengthChange}
                                            className="w-32"
                                        />
                                        <span className="text-sm text-muted-foreground">
                                            {outputLength * 8} bits
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Adjust the output length (1-8192 bytes)
                                    </p>
                                </div>

                                <div className="relative">
                                    <pre className="bg-muted/50 p-4 rounded-md overflow-x-auto text-sm font-mono">
                                        {isLoading ? (
                                            <div className="flex items-center space-x-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>Generating hash...</span>
                                            </div>
                                        ) : hash ? (
                                            hash
                                        ) : (
                                            <span className="text-muted-foreground">
                                                {activeTab === 'text'
                                                    ? 'Enter some text or use the sample text to generate a hash.'
                                                    : 'Upload a file to generate its hash.'}
                                            </span>
                                        )}
                                    </pre>
                                    {hash && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute top-2 right-2 h-8 w-8"
                                            onClick={copyToClipboard}
                                            disabled={isLoading}
                                        >
                                            {isCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                            <span className="sr-only">Copy hash</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter className="flex justify-between">
                            <div className="text-sm text-muted-foreground">
                                {hash ? `Hash length: ${hash.length / 2} bytes (${hash.length} hex chars)` : ''}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    resetInput();
                                    setHash('');
                                }}
                                disabled={!input && !file}
                            >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Reset
                            </Button>
                        </CardFooter>
                    </Card>
                </div>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={shake256HashContent.title}
                        description={shake256HashContent.description}
                        content={shake256HashContent.content}
                        faqs={shake256HashContent.faqs}
                        relatedTools={shake256HashContent.relatedTools}
                    />
                </div>
            </div>
            <Toaster />
        </div>
    );
}